import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import {
    MarketplaceEventActionTypeList,
    SellerActionTypeGroups,
    TimelineActionType,
} from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import createNotification from '../../../utils/createNotification';

export interface TempoDurationValidationResult {
    isValid: boolean;
    errorMessage?: string;
}

const LOI_ORDERED_ACTION_TYPES: TimelineActionType[] = [
    TimelineActionType.BuyerLoiSubmission,
    TimelineActionType.VetValueLoiFormCreation,
    TimelineActionType.BuyerLoiFormReview,
    TimelineActionType.SellerLoiComparison,
];

export class TimelineValidationHelper {
    public static validateNewEvent = () => {};
    public static isEventDuplicated(
        events: TimelineEvent[],
        rowIndex: number,
        name: string,
        stage: number,
        party: TimelineParty
    ): boolean {
        const isDuplicate = events.some(
            (row, index) =>
                index !== rowIndex &&
                row.name === name &&
                row.stage === stage &&
                row.party === party
        );

        if (isDuplicate) {
            createNotification(
                "The combination of 'Event', 'Stage', and 'Party' must be unique.",
                'error'
            );
        }

        return isDuplicate;
    }

    public static areDependenciesValid(
        events: TimelineEvent[],
        updatedRow: TimelineEvent,
        stagesOrder: number[]
    ): boolean {
        const meetingDependencyValidation = this.areMeetingEventDependenciesValid(
            events,
            updatedRow
        );
        if (!meetingDependencyValidation.isValid) {
            createNotification(meetingDependencyValidation.errorMessage, 'error');
            return false;
        }

        if (updatedRow.dependencies && updatedRow.dependencies.length > 0) {
            const nonexistentDependencies = updatedRow.dependencies.filter(
                (dep) =>
                    !events.some((event) => event.id === dep.dependencyEventId)
            );
            if (nonexistentDependencies.length > 0) {
                createNotification(
                    'Some dependencies reference non-existent events.',
                    'error'
                );
                return false;
            }

            const rowStageNextIndex = stagesOrder.indexOf(updatedRow.stage) + 1;
            const followingStages =
                rowStageNextIndex == stagesOrder.length
                    ? []
                    : stagesOrder.slice(rowStageNextIndex);
            const dependenciesFromNextStages =
                followingStages.length == 0
                    ? []
                    : updatedRow.dependencies.filter((dep) =>
                          followingStages.includes(
                              events.find(
                                  (event) => event.id === dep.dependencyEventId
                              )?.stage
                          )
                      );

            const dependentEvents = events.reduce((arr, event) => {
                if (
                    event.dependencies &&
                    event.dependencies.length > 0 &&
                    event.dependencies.some(
                        (d) =>
                            d.dependencyEventId == updatedRow.id &&
                            event.stage != updatedRow.stage
                    )
                ) {
                    arr.push(event);
                }
                return arr;
            }, [] as TimelineEvent[]);

            if (
                dependenciesFromNextStages.length > 0 &&
                dependentEvents.length > 0
            ) {
                createNotification(
                    'Invalid dependencies and dependent combination.',
                    'error'
                );
                return false;
            }
        }

        return true;
    }

    public static isNewStageActionValid(
        newStage: number,
        currentEvent: TimelineEvent,
        events: TimelineEvent[]
    ): boolean {
        const stageUniqueActions = [
            TimelineActionType.BuyerExpressionOfInterestWithRange,
        ];

        const stageUniqActionTypeGroup = [
            {
                actionTypeGroup: SellerActionTypeGroups.BuyerSelection,
                party: TimelineParty.Seller,
            },
        ];

        const isStageUniqueAction = stageUniqueActions.some(
            (s) => s === currentEvent.linkedAction?.actionType
        );

        const currentActionTypeDefinition = MarketplaceEventActionTypeList.find(
            (f) => f.id === currentEvent.linkedAction?.actionType
        );

        const currentGroup = currentActionTypeDefinition?.group;

        const isStageUniqueActionTypeGroup = stageUniqActionTypeGroup.some(
            (s) =>
                currentGroup &&
                s.actionTypeGroup === currentActionTypeDefinition?.group &&
                s.party == currentActionTypeDefinition?.party
        );

        if (isStageUniqueActionTypeGroup) {
            const stageEvents = events.filter(
                (f) => f.id !== currentEvent.id && f.stage === newStage
            );

            const doesActionTypeGroupAlreadyExist = stageEvents.some(
                (e) =>
                    e.id !== currentEvent.id &&
                    e.party === currentEvent.party &&
                    e.linkedAction &&
                    MarketplaceEventActionTypeList.find(
                        (f) => f.id === e.linkedAction?.actionType
                    )?.group === currentGroup &&
                    e.stage === newStage
            );

            if (doesActionTypeGroupAlreadyExist) {
                createNotification(
                    'A unique option is duplicated within the same stage',
                    'error'
                );

                return false;
            }
        }

        if (isStageUniqueAction) {
            const stageEvents = events.filter(
                (f) => f.id !== currentEvent.id && f.stage === newStage
            );
            const doesActionAlreadyExist = stageEvents.some(
                (s) =>
                    s.linkedAction?.actionType ===
                    currentEvent.linkedAction?.actionType
            );

            if (doesActionAlreadyExist) {
                createNotification(
                    'A unique action is duplicated within the same stage',
                    'error'
                );

                return false;
            }
        }

        return true;
    }

    public static areMeetingEventDependenciesValid(
        events: TimelineEvent[],
        updatedRow: TimelineEvent
    ): { isValid: boolean; errorMessage?: string } {
        const actionType = updatedRow.linkedAction?.actionType;

        if (
            actionType !== TimelineActionType.SellerProvideMeetingAvailability &&
            actionType !== TimelineActionType.SellerBuyerAgreeOnMeetingTimes &&
            actionType !== TimelineActionType.SellerBuyerMeetingsPeriod
        ) {
            return { isValid: true };
        }

        if (actionType === TimelineActionType.SellerBuyerAgreeOnMeetingTimes) {
            if (updatedRow.dependencies.length !== 1) {
                return {
                    isValid: false,
                    errorMessage:
                        '"Agree on Meeting Times" must have exactly one dependency on "Provide Meeting Availability".',
                };
            }

            const dependency = updatedRow.dependencies[0];
            const depEvent = events.find(
                (e) => e.id === dependency.dependencyEventId
            );

            if (
                depEvent?.linkedAction?.actionType !==
                TimelineActionType.SellerProvideMeetingAvailability
            ) {
                return {
                    isValid: false,
                    errorMessage:
                        '"Agree on Meeting Times" must have a dependency only on "Provide Meeting Availability".',
                };
            }
        }

        if (actionType === TimelineActionType.SellerBuyerMeetingsPeriod) {
            if (updatedRow.dependencies.length !== 1) {
                return {
                    isValid: false,
                    errorMessage:
                        '"Meetings Period" must have exactly one dependency on "Agree on Meeting Times".',
                };
            }

            const dependency = updatedRow.dependencies[0];
            const depEvent = events.find(
                (e) => e.id === dependency.dependencyEventId
            );

            if (
                depEvent?.linkedAction?.actionType !==
                TimelineActionType.SellerBuyerAgreeOnMeetingTimes
            ) {
                return {
                    isValid: false,
                    errorMessage:
                        '"Meetings Period" must have a dependency only on "Agree on Meeting Times".',
                };
            }
        }

        return { isValid: true };
    }

    public static areTemposDurationsValid(
        slowDuration: number,
        mediumDuration: number,
        fastDuration: number
    ): TempoDurationValidationResult {
        if (slowDuration < 0 || mediumDuration < 0 || fastDuration < 0) {
            return {
                isValid: false,
                errorMessage: 'Duration values cannot be negative.',
            };
        }

        if (slowDuration < mediumDuration) {
            return {
                isValid: false,
                errorMessage: `Slow tempo duration (${slowDuration} days) cannot be less than Medium tempo duration (${mediumDuration} days).`,
            };
        }

        if (mediumDuration < fastDuration) {
            return {
                isValid: false,
                errorMessage: `Medium tempo duration (${mediumDuration} days) cannot be less than Fast tempo duration (${fastDuration} days).`,
            };
        }

        return { isValid: true };
    }

    public static areLoiEventsOrderValid(
        events: TimelineEvent[],
        initialEvents: TimelineEvent[]
    ): boolean {
        const currentById = new Map(events.map((e) => [e.id, e]));
        const initialById = new Map(initialEvents.map((e) => [e.id, e]));

        const getDependencyIds = (event: TimelineEvent): Set<string> => {
            const ids = new Set<string>();
            for (const dep of event.dependencies ?? []) {
                if (dep.dependencyEventId) {
                    ids.add(dep.dependencyEventId);
                }
            }
            return ids;
        };

        const collectAncestors = (event: TimelineEvent): Set<string> => {
            const ancestors = new Set<string>();
            const stack: string[] = Array.from(getDependencyIds(event));
            while (stack.length > 0) {
                const id = stack.pop() as string;
                if (ancestors.has(id)) {
                    continue;
                }
                ancestors.add(id);
                const parent = currentById.get(id);
                if (!parent) {
                    continue;
                }
                Array.from(getDependencyIds(parent)).forEach((parentDepId) => {
                    if (!ancestors.has(parentDepId)) {
                        stack.push(parentDepId);
                    }
                });
            }
            return ancestors;
        };

        const areDependencyIdsEqual = (a: Set<string>, b: Set<string>): boolean => {
            if (a.size !== b.size) {
                return false;
            }
            let equal = true;
            a.forEach((id) => {
                if (!b.has(id)) {
                    equal = false;
                }
            });
            return equal;
        };

        const findEventByActionType = (
            list: TimelineEvent[],
            actionType: TimelineActionType
        ) => list.find((e) => e.linkedAction?.actionType === actionType);

        const getActionTypeName = (actionType: TimelineActionType): string =>
            MarketplaceEventActionTypeList.find((a) => a.id === actionType)?.name ??
            '';

        for (let i = 0; i < LOI_ORDERED_ACTION_TYPES.length - 1; i++) {
            const earlierType = LOI_ORDERED_ACTION_TYPES[i];
            const laterType = LOI_ORDERED_ACTION_TYPES[i + 1];

            const earlier = findEventByActionType(events, earlierType);
            const later = findEventByActionType(events, laterType);
            if (!earlier || !later) {
                continue;
            }

            if (collectAncestors(later).has(earlier.id)) {
                continue;
            }

            const initialEarlier = initialById.get(earlier.id);
            const initialLater = initialById.get(later.id);
            const pairPreservedFromInitial =
                !!initialEarlier &&
                !!initialLater &&
                initialEarlier.linkedAction?.actionType === earlierType &&
                initialLater.linkedAction?.actionType === laterType &&
                areDependencyIdsEqual(
                    getDependencyIds(earlier),
                    getDependencyIds(initialEarlier)
                ) &&
                areDependencyIdsEqual(
                    getDependencyIds(later),
                    getDependencyIds(initialLater)
                );

            if (pairPreservedFromInitial) {
                continue;
            }

            createNotification(
                `"${getActionTypeName(laterType)}" must come after "${getActionTypeName(earlierType)}" in the timeline (via dependencies).`,
                'error'
            );
            return false;
        }

        return true;
    }

    public static areAllEventsTempoDurationsValid(
        events: TimelineEvent[],
        showNotification: boolean = true
    ): boolean {
        for (const event of events) {
            const validationResult = this.areTemposDurationsValid(
                event.slowTempoDurationDays,
                event.mediumTempoDurationDays,
                event.fastTempoDurationDays
            );

            if (!validationResult.isValid) {
                if (showNotification) {
                    createNotification(
                        `Event "${event.name}": ${validationResult.errorMessage}`,
                        'error'
                    );
                }
                return false;
            }
        }

        return true;
    }
}
